import { gql } from '@apollo/client';

export const jediReenableSlave = gql`
    mutation jediReenableSlave(
        $autoPilotSettingsKey: Int!
    ) {
        jediReenableSlave(input: {
            autoPilotSettingsKey: $autoPilotSettingsKey
        }) {
            success
        }
    }
`;

export const jediUpdateShortCircuited = gql`
    mutation jediUpdateShortCircuited(
        $autoPilotSettingsKey: Int!
        $shortCircuited: Boolean!
        $reason: String
    ) {
        jediUpdateShortCircuited(input: {
            autoPilotSettingsKey: $autoPilotSettingsKey
            shortCircuited: $shortCircuited
            reason: $reason
        }) {
            success
            shortCircuited
        }
    }
`;

export const jediResolveOverAllocation = gql`
    mutation jediResolveOverAllocation(
        $autoPilotSettingsKey: Int!
    ) {
        jediResolveOverAllocation(input: {
            autoPilotSettingsKey: $autoPilotSettingsKey
        }) {
            success
        }
    }
`;

export const jediUpdateExecutionType = gql`
    mutation exuctionTypeUpdate(
        $autoPilotSettingsKey: Int!
        $useManualExecution: Boolean!
    ) {
        exuctionTypeUpdate(input: {
            autoPilotSettingsKey: $autoPilotSettingsKey
            useManualExecution: $useManualExecution
        }) {
            success
        }
    }
`;

export const balancingDecisionsQuery = gql`
    query balancingDecisionsGet($autoPilotSettingsKey: Int!) {
        balancingDecisionsGet(input: {
            autoPilotSettingsKey: $autoPilotSettingsKey
        }) {
            balancingDecisions {
                symbol
                positionType
                orderSide
                reason
                kind
                quantity
                asset {
                    pictureUrl
                }
                price
            }
            balancingParameters
            overAllocatedAmount
        }
    }
`;

export interface BalancingDecision {
    symbol: string;
    positionType: string;
    orderSide: string;
    reason: string;
    kind: string;
    quantity: number;
    asset: {
        pictureUrl: string;
    };
    price: number;
};

export interface BalancingParameters {
    brokerParameters: {
        fundsAvailable: number;
        unsettledFunds: number;
        assetBuyingPowerData: {
            symbol: string;
            buyingPower: number;
            positionType: string;
        }[];
        positionShareSizeData: {
            positionType: string;
            symbol: string;
            minShareSize: number;
        }[];
        ordersToPreventPDTInfraction: any[];
        minFractionalBuyAmount: number;
        mayRoundFractionalCorner: boolean;
        subDollarMinOrderRequirement: number;
        institutionSellWholeFractionOnly: boolean;
        institutionAssetBlacklist: {
            symbol: string;
            assetType: string;
        }[];
    };
    symbolBlacklist: string[];
    positionTypeWhitelist: string[];
    masterPositions: MasterPosition[];
    slavePositions: SlavePosition[];
    brokerPositions: BrokerPosition[];
    settingParameters: SettingParameters;
    masterValuation: number;
    slaveValuation: number;
    valuationRatioOfSlaveToMaster: number;
    minBuyBalanceDecisionAmount: number;
    minSellBalanceDecisionAmount: number;
    inFlightAPOrders: any[];
}

export interface MasterPosition {
    portfolioKey: number;
    timeseriesKey: number;
    autoPilotSettingsKey: number;
    symbol: string;
    totalNetQuantity: number;
    positionType: string;
    averageUnitCostBasis: number;
    optionDetails: any;
    unrealizedProfitLoss: number;
    unrealizedProfitLossPercent: number;
    unrealizedReturn: number;
    marketQuote: {
        unitPrice: number;
    }
}

export interface SlavePosition {
    portfolioKey: number;
    timeseriesKey: number;
    autoPilotSettingsKey: number;
    symbol: string;
    totalNetQuantity: number;
    positionType: string;
    averageUnitCostBasis: number;
    optionDetails: any;
    unrealizedProfitLoss: number;
    unrealizedProfitLossPercent: number;
    unrealizedReturn: number;
    marketQuote: {
        unitPrice: number;
    }
}

export interface BrokerPosition {
    portfolioKey: number;
    timeseriesKey: number;
    autoPilotSettingsKey: number;
    symbol: string;
    totalNetQuantity: number;
    positionType: string;
    averageUnitCostBasis: number;
    optionDetails: any;
    unrealizedProfitLoss: number;
    unrealizedProfitLossPercent: number;
    unrealizedReturn: number;
    marketQuote: {
        unitPrice: number;
    }
}

export interface SettingParameters {
    allocationAmount: number;
    amountPending: number;
    slaveAutopilotPortfolioCash: number;
    masterCashPositionAmount: number;
    maxMasterPositionCount: number;
}

// New V2 Balancing Types
export const balancingConnectionQuery = gql`
    query autoPilotSettingsBalancingConnection($autoPilotSettingsKey: Int!) {
        autoPilotSettingsGet(input: {
            autoPilotSettingsKeys: [$autoPilotSettingsKey]
        }) {
            autoPilotSettings {
                setupStatus
                balancingConnection {
                    overAllocationAmount
                    decisions {
                        kind
                        assetKey
                        targetQuantity
                        currentQuantity
                        quantityToExecute
                        amountToExecute
                        price
                        positionType
                        orderSide
                        workRequiredAttributionState
                        rulesPassed {
                            passed
                            type
                            reason
                            workRequiredAttributionState
                        }
                        rulesFailed {
                            passed
                            type
                            reason
                            workRequiredAttributionState
                        }
                        targetPosition {
                            targetQuantity
                            currentQuantity
                            type
                            assetKey
                            symbol
                            price
                            targetMarketValue
                            currentMarketValue
                            pinnedTransactionAmount
                            pinnedTransactionQuantity
                            modifiedTargetQuantity
                            modifications {
                                modifiedTargetQuantity
                                type
                                modified
                                reason
                            }
                            asset {
                                pictureUrl
                            }
                        }
                    }
                    decisionContext {
                        snapshot {
                            valuationRatio
                            type
                            brokerName
                            cash
                            realizedProfitLoss
                            subDollarMinOrderRequirement
                            symbolBlacklist
                            portfolioKey
                        }
                        assetToleranceData {
                            assetKey
                            tolerance {
                                assetKey
                                minBuyAmount
                                minSellAmount
                            }
                        }
                        failureAnalysis {
                            assetKey
                            data {
                                failureAttribution {
                                    brokerName
                                    slavePortfolioKey
                                    symbol
                                    assetKey
                                    failureAttribution
                                    failureAttributionReason
                                    fractionalTrade
                                    isRetryable
                                    tradeAttempts
                                    orderSide
                                    autoPilotOrderKey
                                    errorMessage
                                }
                            }
                        }
                        fundsAvailable
                        unsettledFunds
                        subDollarMinOrderRequirement
                        approvalType
                        assetBuyingPowerData {
                            symbol
                            buyingPower
                            positionType
                        }
                    }
                }
            }
        }
    }
`;

export interface ModificationResult {
    modifiedTargetQuantity: number;
    type: string;
    modified: boolean;
    reason?: string;
}

export interface RuleValidationResult {
    passed: boolean;
    type: string;
    reason?: string;
    workRequiredAttributionState?: string;
}

export interface TargetPosition {
    targetQuantity: number;
    currentQuantity: number;
    type: string;
    assetKey: number;
    symbol: string;
    price: number;
    targetMarketValue: number;
    currentMarketValue: number;
    pinnedTransactionAmount?: number;
    pinnedTransactionQuantity?: number;
    modifiedTargetQuantity: number;
    modifications: ModificationResult[];
    asset?: {
        pictureUrl: string;
    };
}

export interface BalancingDecisionV2 {
    kind: 'IExecuteOrderBalancingDecision' | 'INoopBalancingDecision';
    assetKey: number;
    targetQuantity: number;
    currentQuantity: number;
    quantityToExecute: number;
    amountToExecute: number;
    price: number;
    positionType: string;
    orderSide: string;
    workRequiredAttributionState: string;
    rulesPassed: RuleValidationResult[];
    rulesFailed: RuleValidationResult[];
    targetPosition: TargetPosition;
}

export interface AssetTolerance {
    assetKey: number;
    minBuyAmount: number;
    minSellAmount: number;
}

export interface AssetToleranceData {
    assetKey: number;
    tolerance: AssetTolerance;
}

export interface FailedOrderData {
    brokerName: string;
    slavePortfolioKey: number;
    symbol: string;
    assetKey: number;
    failureAttribution: string;
    failureAttributionReason: string;
    fractionalTrade: boolean;
    isRetryable: boolean;
    tradeAttempts: number;
    orderSide: string;
    autoPilotOrderKey: number;
    errorMessage: string;
}

export interface FailureAttributionCacheData {
    failureAttribution: FailedOrderData[];
}

export interface FailureAnalysisData {
    assetKey: number;
    data: FailureAttributionCacheData | null;
}

export interface BalancingSnapshot {
    valuationRatio: number;
    type: string;
    brokerName: string;
    cash: number;
    realizedProfitLoss: number;
    subDollarMinOrderRequirement: number;
    symbolBlacklist: string[];
    portfolioKey: number;
}

export interface AssetBuyingPowerData {
    symbol: string;
    buyingPower: number;
    positionType: string;
}

export interface DecisionContext {
    snapshot: BalancingSnapshot;
    assetToleranceData: AssetToleranceData[];
    failureAnalysis: FailureAnalysisData[];
    fundsAvailable: number;
    unsettledFunds: number;
    subDollarMinOrderRequirement: number;
    approvalType: string;
    // List is non-null but elements are nullable in the schema ([AssetBuyingPowerData]!).
    assetBuyingPowerData: (AssetBuyingPowerData | null)[];
}

// AutoPilot Orders Query
export const autoPilotOrdersQuery = gql`
    query slaveAutoPilotOrdersGet(
        $autoPilotSettingsKey: Int!
        $userKey: Int!
        $limit: Int!
        $offset: Int!
    ) {
        slaveAutoPilotOrdersGet(input: {
            limit: $limit
            offset: $offset
            autoPilotSettingsKey: $autoPilotSettingsKey
            userKey: $userKey
            orderStatuses: null
        }) {
            autoPilotOrderKey
            asset {
                symbol
                pictureUrl
            }
            transactionAmount
            orderState
            orderStatus
            failureReason
            orderSide
            quantity
            balancingDecisionQuantity
            createdAt
            transmittedAt
            fullfilledAt
            autoPilotOrderActivities(input: { limit: 10, offset: 0 }) {
                data
                value
                errorMessage
                activityState
                createdAt
            }
        }
    }
`;

export interface AutoPilotOrderActivity {
    data: string;
    value: string;
    errorMessage: string;
    activityState: string;
    createdAt: string;
}

// Cancels PENDING AutoPilotOrders (sets them to CANCELED)
export const cancelSlaveAutoPilotOrdersMutation = gql`
    mutation slaveAutoPilotOrdersCancel(
        $userKey: Int!
        $autoPilotOrderKeys: [Int!]!
        $reason: String
    ) {
        slaveAutoPilotOrdersCancel(input: {
            userKey: $userKey
            autoPilotOrderKeys: $autoPilotOrderKeys
            reason: $reason
        }) {
            cancelledAutoPilotOrderKeys
        }
    }
`;

export interface AutoPilotOrder {
    autoPilotOrderKey: number;
    asset: {
        symbol: string;
        pictureUrl: string;
    };
    transactionAmount: number;
    orderState: string;
    orderStatus: string;
    failureReason: string;
    orderSide: string;
    quantity: number;
    balancingDecisionQuantity: number;
    createdAt: string;
    transmittedAt: string;
    fullfilledAt: string;
    autoPilotOrderActivities: AutoPilotOrderActivity[];
}

export const balancingActivitiesQuery = gql`
    query balancingActivitiesGet($autoPilotSettingsKey: Int!, $limit: Int!, $offset: Int!) {
        autoPilotSettingsGet(input: { autoPilotSettingsKeys: [$autoPilotSettingsKey] }) {
            autoPilotSettings {
                autoPilotSettingsKey
                autoPilotBalancingActivities(input: { limit: $limit, offset: $offset }) {
                    autoPilotBalancingActivityKey
                    autoPilotSettingsKey
                    valuationRatio
                    masterValuation
                    slaveValuation
                    positionMarketValueAmount
                    masterCashPositionAmount
                    slaveCashPositionAmount
                    data
                    createdAt
                    distanceToTargetPositions
                    balancingStatus
                    reason
                    buyingPowerNecessaryForEquilibrium
                    hadInsufficientLiquidity
                    userApprovalKey
                    balanceDecisions {
                        kind
                        quantity
                        symbol
                        positionType
                        orderSide
                        reason
                        isExecuteOrderDecision
                        price
                        positionEffect
                    }
                    autoPilotOrders {
                        symbol
                        orderSide
                        positionEffect
                        quantity
                        averagePrice
                        transactionAmount
                        orderStatus
                        orderState
                        fullfilledAt
                        transmittedAt
                        positionType
                        failureReason
                        balancingDecisionQuantity
                        createdAt
                        autoPilotOrderActivities(input: { limit: 30, offset: 0 }) {
                            data
                            value
                            errorMessage
                            activityState
                            createdAt
                        }
                    }
                }
            }
        }
    }
`;

export interface BalancingActivityOrder {
    symbol: string;
    orderSide: string;
    positionEffect: string;
    quantity: number;
    balancingDecisionQuantity: number;
    averagePrice: number;
    transactionAmount: number;
    orderStatus: string;
    orderState: string;
    fullfilledAt: string;
    transmittedAt: string;
    positionType: string;
    failureReason: string;
    createdAt: string;
    autoPilotOrderActivities?: AutoPilotOrderActivity[];
}

export interface BalancingActivityDecision {
    kind: string;
    quantity: number;
    symbol: string;
    positionType: string;
    orderSide: string;
    reason: string;
    isExecuteOrderDecision: boolean;
    price: number;
    positionEffect: string;
}

// Position history (PositionHistory rows joined with the source Order and the
// matching balancing decision JSON) for a single symbol on a given autoPilotSettings.
// Powers the "Position History" tab in the BalancingDecisionsTableV2 detail modal.
export const jediPositionHistoryWithDecisionsQuery = gql`
    query jediPositionHistoryWithDecisions(
        $autoPilotSettingsKey: Int!
        $symbol: String!
        $positionType: POSITION_TYPE
    ) {
        jediPositionHistoryWithDecisions(input: {
            autoPilotSettingsKey: $autoPilotSettingsKey
            symbol: $symbol
            positionType: $positionType
        }) {
            rows {
                sequenceNumber
                computedAt
                totalNetQuantity
                averagePrice
                quantity
                amountSpent
                placedAt
                autoPilotSettingsKey
                orderSide
                portfolioKey
                positionEffect
                assetKey
                decision {
                    kind
                    assetKey
                    targetQuantity
                    currentQuantity
                    quantityToExecute
                    amountToExecute
                    price
                    positionType
                    orderSide
                    rulesPassed {
                        passed
                        type
                        reason
                    }
                    rulesFailed {
                        passed
                        type
                        reason
                    }
                    targetPosition {
                        targetQuantity
                        currentQuantity
                        type
                        assetKey
                        symbol
                        price
                        targetMarketValue
                        currentMarketValue
                        pinnedTransactionAmount
                        pinnedTransactionQuantity
                        modifiedTargetQuantity
                        modifications {
                            modifiedTargetQuantity
                            type
                            modified
                            reason
                        }
                        asset {
                            pictureUrl
                        }
                    }
                }
                fundsAvailable
            }
        }
    }
`;

export interface JediPositionHistoryDecisionRow {
    sequenceNumber: number | null;
    computedAt: string | null;
    totalNetQuantity: number | null;
    averagePrice: number | null;
    quantity: number | null;
    amountSpent: number | null;
    placedAt: string | null;
    autoPilotSettingsKey: number | null;
    orderSide: string | null;
    portfolioKey: number | null;
    positionEffect: string | null;
    assetKey: number | null;
    decision: BalancingDecisionV2 | null;
    fundsAvailable: number | null;
}

export interface BalancingActivity {
    autoPilotBalancingActivityKey: number;
    autoPilotSettingsKey: number;
    valuationRatio: number;
    masterValuation: number;
    slaveValuation: number;
    positionMarketValueAmount: number;
    masterCashPositionAmount: number;
    slaveCashPositionAmount: number;
    data: any;
    createdAt: string;
    distanceToTargetPositions: number;
    balancingStatus: string;
    reason: string;
    buyingPowerNecessaryForEquilibrium: number;
    hadInsufficientLiquidity: boolean;
    userApprovalKey: number;
    balanceDecisions: BalancingActivityDecision[];
    autoPilotOrders: BalancingActivityOrder[];
}